import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

export const useFetchedValue = <T, A extends any[]>(
    fetch: (...args: [...A]) => Promise<T>,
    doFetch = true,
    ...args: [...A]
): [T, () => Promise<void>, Dispatch<SetStateAction<T>>, any, boolean] => {
    const [value, setValue] = useState<T>(undefined);
    const [error, setError] = useState();
    const [isLoading, setIsLoading] = useState(doFetch);
    const fetchValue = useCallback(async () => {
        if (doFetch) {
            await (async () => {
                try {
                    setIsLoading(true);
                    setError(undefined);
                    setValue(await fetch(...args));
                } catch (ex) {
                    setError(ex);
                    setValue(null);
                } finally {
                    setIsLoading(false);
                }
            })();
        } else {
            setValue(value ?? null);
        }
    }, [...args, doFetch]);
    useEffect(() => {
        fetchValue();
    }, [...args, doFetch]);
    return [value, fetchValue, setValue, error, isLoading];
};

export const useFetchedValues = <T, A extends any[]>(
    fetch: (...args: [...A]) => Promise<Array<T>>,
    doFetch = true,
    ...args: [...A]
): [Array<T>, () => Promise<void>, Dispatch<SetStateAction<Array<T>>>, any, boolean] => {
    const [values, setValues] = useState<Array<T>>([]);
    const [error, setError] = useState();
    const [isLoading, setIsLoading] = useState(doFetch);
    const fetchValues = useCallback(async () => {
        if (doFetch) {
            await (async () => {
                try {
                    setIsLoading(true);
                    setError(undefined);
                    setValues(await fetch(...args));
                } catch (ex) {
                    setError(ex);
                    setValues([]);
                } finally {
                    setIsLoading(false);
                }
            })();
        } else {
            setValues(values ?? []);
        }
    }, [...args, doFetch]);
    useEffect(() => {
        fetchValues();
    }, [...args, doFetch]);
    return [values, fetchValues, setValues, error, isLoading];
};
